import { PDFDocument, StandardFonts } from "pdf-lib";

export const renderPdfWithFields = async (
  data: any,
  patientData: any
) => {
  const base64 = data.templateMetadata.templateImage;
  const fields = data.templateMetadata.fields || [];

  // ✅ base64 → bytes
  const pdfBytes = Uint8Array.from(
    atob(base64),
    (c) => c.charCodeAt(0)
  );

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const pages = pdfDoc.getPages();

  // ✅ VALUE MAPPING
  const getValueFromPatient = (key: string, patientData: any) => {
    const map: Record<string, any> = {
      patientName: patientData?.pmaster?.patientName,
      firstName: patientData?.pmaster?.firstName,
      lastName: patientData?.pmaster?.lastName,
      dob: patientData?.pmaster?.dob,
      gender: patientData?.pmaster?.gender,
      emrNo: patientData?.pmaster?.patientId,
      emiratesId: patientData?.pmaster?.emiratesId,

      doctorName: patientData?.encounter?.doctorName,
      clinicName: patientData?.encounter?.clinicName,
      departmentName: patientData?.encounter?.departmentName,
      purpose: patientData?.encounter?.purposeName,
      visitDate: patientData?.encounter?.createdDate,

      insuranceName: patientData?.insurancePatient?.insurance_name,
      policyNo: patientData?.insurancePatient?.policyNo,

      age: patientData?.pmaster?.age,
    };

    return map[key] ?? "";
  };

  // ✅ LOOP THROUGH FIELDS
  for (const field of fields) {
    const page = pages[field.pageIndex || 0];
    if (!page) continue;

    const pageHeight = page.getHeight();
    const pageWidth = page.getWidth();

    // 🌟 Proportional mapping: aligns UI coordinates (210x297) physically to actual PDF dimensions 
    const pdfX = (field.xMM / 210) * pageWidth;
    const topPdf = pageHeight - (field.yMM / 297) * pageHeight;
    const pdfWidth = (field.widthMM / 210) * pageWidth;
    const pdfHeight = (field.heightMM / 297) * pageHeight;

    // ✅ IMAGE / SIGNATURE
    if (
      field.type === "image" ||
      (field.originalType === "signature" && field.src)
    ) {
      if (field.src) {
        try {
          const base64Data = field.src.replace(
            /^data:image\/(png|jpeg|jpg);base64,/,
            ""
          );

          const image =
            field.src.includes("jpg") || field.src.includes("jpeg")
              ? await pdfDoc.embedJpg(base64Data)
              : await pdfDoc.embedPng(base64Data);

          page.drawImage(image, {
            x: pdfX,
            y: topPdf - pdfHeight,
            width: pdfWidth,
            height: pdfHeight,
          });
        } catch (e) {
          console.error("Failed to embed image", e);
        }
      }
      continue;
    }

    // ✅ TEXT RENDERING (FIXED)
    const fontSize = field.fontSize || 12;

  let text =
  field.value !== undefined && field.value !== null && field.value !== ""
    ? field.value
    : field.keyName
    ? getValueFromPatient(field.keyName, patientData)
    : "";

    text = String(text || "");

    // ✅ trim overflow (safe)
    while (
      text.length > 0 &&
      font.widthOfTextAtSize(text, fontSize) > pdfWidth
    ) {
      text = text.slice(0, -1);
    }

    // ✅ ALIGNMENT BASED ON COMPONENT TYPE
    let textY = topPdf - fontSize; // Default: Pinned to top for textarea/text

    if (field.type === "underline") {
      // Underline elements sit at the *bottom* of their bounding box in the UI
      textY = topPdf - pdfHeight + 2; // +2pt buffer off the line
    }

    // ✅ DRAW TEXT
    page.drawText(text, {
      x: pdfX,
      y: textY,
      size: fontSize,
      font,
      maxWidth: pdfWidth,
    });
  }

  // ✅ SAVE PDF
  const modifiedPdf = await pdfDoc.save();

  return new Blob([new Uint8Array(modifiedPdf)], {
    type: "application/pdf",
  });
};